import { ENV } from "../env";
/**
 * API client for backend - base URL and fetch with credentials
 * On 401 (session invalid/expired), یک رویداد NEED_LOGIN_EVENT می‌فرستد تا مودال ورود باز شود.
 * روی /stories/:id درخواست‌های خواندن استوری 401 را بدون باز کردن مودال ورود عبور می‌دهند.
 */

import { isPublicStoryApiRequest } from "../utils/storyPublicAccess";
import { isUserFacingText } from "../utils/userMessage";

const BASE_URL =
  ENV.VITE_BACKEND_API_URI || "https://hami-back.paziresh24.com/api/external";

function resolveBackendOrigin() {
  if (ENV.VITE_BACKEND_WS_ORIGIN) {
    return ENV.VITE_BACKEND_WS_ORIGIN.replace(/\/$/, "");
  }
  if (ENV.VITE_BACKEND_PROXY_TARGET) {
    return ENV.VITE_BACKEND_PROXY_TARGET.replace(/\/$/, "");
  }
  if (BASE_URL.startsWith("/")) {
    return typeof window !== "undefined" ? window.location.origin : "";
  }
  try {
    return new URL(BASE_URL).origin;
  } catch {
    return "https://hami-back.paziresh24.com";
  }
}

export function getApiUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  const base = BASE_URL.replace(/\/$/, "");
  if (base.startsWith("/")) {
    // the site runs with trailingSlash: true — without the slash every call through this
    // same-origin door was answered with a 308 first and sent again (one more round trip each)
    return `${base}${p.replace(/^([^?#]*?)\/?(?=[?#]|$)/, "$1/")}`;
  }
  return `${base}${p}`;
}

function isOurApiUrl(url) {
  try {
    if (BASE_URL.startsWith("/") && typeof window !== "undefined") {
      const req = typeof url === "string" ? new URL(url, window.location.origin) : url;
      return req.pathname.startsWith(BASE_URL);
    }
    const base = new URL(BASE_URL);
    const req = typeof url === "string" ? new URL(url, base) : url;
    return req.host === base.host;
  } catch {
    return false;
  }
}

const NEED_TWO_FA_EVENT = "hami:needTwoFa";

/** نشست نامعتبر یا منقضی — نمایش مودال ورود (به‌جای هدایت به URL لاگین) */
export const NEED_LOGIN_EVENT = "hami:needLogin";

function installAuthInterceptor() {
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const method = init?.method || (typeof input === "object" && input?.method) || "GET";
    return originalFetch.call(this, input, init).then(async (res) => {
      const url = typeof input === "string" ? input : input?.url ?? "";
      if (res.status === 401 && isOurApiUrl(url)) {
        if (!isPublicStoryApiRequest(url, method)) {
          window.dispatchEvent(new CustomEvent(NEED_LOGIN_EVENT));
        }
        return res;
      }
      if (res.status === 403 && isOurApiUrl(url)) {
        try {
          const clone = res.clone();
          const data = await clone.json().catch(() => ({}));
          if (data && data.needTwoFa === true) {
            window.dispatchEvent(new CustomEvent(NEED_TWO_FA_EVENT));
          }
        } catch {
          // ignore
        }
      }
      return res;
    });
  };
}

export { NEED_TWO_FA_EVENT };

installAuthInterceptor();

/** WebSocket URL for real-time messages (wss when page is HTTPS, ws when HTTP). */
export function getWebSocketUrl() {
  const origin = resolveBackendOrigin();
  try {
    const u = new URL(origin || "https://hami-back.paziresh24.com");
    const protocol = u.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${u.host}/ws`;
  } catch {
    return "wss://hami-back.paziresh24.com/ws";
  }
}

const API_SUCCESS_STATUSES = new Set(["SUCCESS", "OK", "UPDATED", "CREATED"]);
const API_ERROR_STATUSES = new Set(["ERROR", "FAIL", "FAILED"]);

/** پاسخ موفق API (مثلاً PUT تیکت با status: UPDATED) */
export function isApiSuccessResponse(res, data) {
  if (!res?.ok) return false;
  const status = String(data?.status ?? "").toUpperCase();
  if (API_ERROR_STATUSES.has(status)) return false;
  if (API_SUCCESS_STATUSES.has(status)) return true;
  return res.ok && !data?.status;
}

/** پیام خام API را برای نمایش به کاربر آماده می‌کند؛ پیام‌های انگلیسی عمومی را حذف می‌کند */
export function normalizeApiUserMessage(message) {
  if (!message || typeof message !== "string") return null;
  const trimmed = message.trim();
  const lower = trimmed.toLowerCase();
  if (
    lower === "successfully done." ||
    lower === "successfully done" ||
    lower === "success" ||
    lower === "ok"
  ) {
    return null;
  }
  // پیام انگلیسی سرور به کاربر نمی‌رسد. متن فارسیِ همان کار جایش می‌نشیند.
  if (!isUserFacingText(trimmed)) return null;
  return trimmed;
}

export async function apiFetch(path, options = {}) {
  const url = getApiUrl(path);
  const res = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  return res;
}
